import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

import {
    type ContactPerson as ContactPersonApi,
    type PhoneNumber as PhoneNumberApi,
    PhoneType,
} from '@/generated/GeneratedApiTypes';

interface ContactPersonProps {
    contactPerson: ContactPersonApi;
}
const COLON = ': ';

const getFormattedPhoneNumber = (phoneNumber: string) => {
    try {
        const phoneUtil = PhoneNumberUtil.getInstance();
        const parsed = phoneUtil.parse(phoneNumber, '');
        return phoneUtil.format(parsed, PhoneNumberFormat.INTERNATIONAL);
    } catch {
        return phoneNumber;
    }
};

const ContactPerson: React.FC<ContactPersonProps> = ({ contactPerson }) => {
    const { name, email, phone_numbers: phoneNumbers } = contactPerson;

    return (
        <>
            <div className={'text-color-gray margin-top-10'}>
                <FormattedMessage id={'intl-msg:vehicle-diagnostics.dialog.contactPerson'} />
            </div>
            <div>{name}</div>
            <div>
                <FormattedMessage id={'intl-msg:vehicle-diagnostics.dialog.email'} />
                {COLON}
                <a href={`mailto:${email}`}>{email}</a>
            </div>
            <PhoneNumbers phoneNumbers={phoneNumbers} />
        </>
    );
};

const PhoneNumbers: React.FC<{ phoneNumbers: PhoneNumberApi[] }> = ({ phoneNumbers }) =>
    useMemo(() => {
        if (phoneNumbers.length === 0) {
            // eslint-disable-next-line id-denylist
            return <PhoneNumberElement phone={{ type: PhoneType.PHONE_TYPE_LAND_LINE, number: '-' }} />;
        }
        if (phoneNumbers.length === 1) {
            return <PhoneNumberElement phone={phoneNumbers[0]} />;
        }
        return phoneNumbers
            .slice()
            .sort((phoneA, phoneB) => {
                if (phoneA.type === phoneB.type && phoneA.number === phoneB.number) {
                    return 0;
                }
                return phoneA.type === PhoneType.PHONE_TYPE_LAND_LINE ? -1 : 1;
            })
            .map((phone) => <PhoneNumberElement key={phone.number} phone={phone} />);
    }, phoneNumbers);

type PhoneNumberElementProps = { phone: PhoneNumberApi };
const PhoneNumberElement: React.FC<PhoneNumberElementProps> = ({ phone }: PhoneNumberElementProps) => (
    <div>
        <FormattedMessage id={`intl-msg:vehicle-diagnostics.dialog.phone-type.${phone.type}`} />
        {COLON}
        {getFormattedPhoneNumber(phone.number)}
    </div>
);

export default ContactPerson;
